export const LISTING_NO_ENDORSEMENT_COPY =
  "Listings are offered by individual community members and are not a parish or diocese endorsement.";

const PRICE_TYPES = Object.freeze(["free", "donation", "hourly", "flat", "negotiable"]);

const SAFETY_LEVEL_BY_SLUG = Object.freeze({
  childcare: "care_sensitive",
  "elder-care": "care_sensitive",
  rides: "care_sensitive",
  legal: "licensed",
  accounting: "licensed",
  "real-estate": "licensed",
  health: "licensed",
});

function trimmed(value) {
  return String(value || "").trim();
}

function compactArray(values) {
  return Array.isArray(values)
    ? values.map((value) => trimmed(value)).filter(Boolean)
    : [];
}

function knownCategory(categories, categoryId) {
  return categories.find((category) => category.id === categoryId);
}

function knownParish(parishes, parishId) {
  return parishes.find((parish) => parish.id === parishId);
}

function providerServiceArea(provider) {
  const city = trimmed(provider?.city);
  const state = trimmed(provider?.state);
  if (city && state) return `${city}, ${state}`;
  return city || state || "";
}

function slugify(value) {
  return trimmed(value)
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 72);
}

function priceAmount(value) {
  const clean = trimmed(value);
  if (!clean) return null;
  const numeric = Number(clean);
  return Number.isFinite(numeric) ? numeric : NaN;
}

export function buildListingDraft(provider = {}) {
  return {
    title: "",
    category_id: "",
    description: "",
    price_type: "negotiable",
    price_amount: "",
    is_remote: true,
    is_in_person: true,
    service_area: providerServiceArea(provider),
    parish_id: provider.parish_id || "",
    languages: compactArray(provider.languages).length ? compactArray(provider.languages) : ["English"],
    availability: "",
    license_info: "",
    photos: 0,
    contact_pref: "in_app",
    agreed: false,
    sensitive_acknowledged: false,
  };
}

export function getCategorySafetyProfile(category) {
  const slug = trimmed(category?.slug);
  const level = category?.safety_level || SAFETY_LEVEL_BY_SLUG[slug] || "standard";
  const requiresCredentialPrompt =
    Boolean(category?.license_prompt_required) ||
    level === "licensed" ||
    level === "care_sensitive" ||
    level === "credential_prompt";
  const requiresSensitiveAcknowledgement = level === "licensed" || level === "care_sensitive";

  const promptByLevel = {
    licensed:
      "Licensed categories require credential details and a stronger reminder that Coptic Neighbor does not verify professional status until admin review.",
    care_sensitive:
      "Care, transportation, and family-support listings require extra caution, credential context where applicable, and admin review before going live.",
    credential_prompt:
      "This category needs credential context before admin review.",
    standard: "",
  };

  return {
    level,
    requiresCredentialPrompt,
    requiresSensitiveAcknowledgement,
    prompt: promptByLevel[level] || "",
  };
}

export function validateListingDraft(draft, { provider, categories = [], parishes = [] } = {}) {
  const errors = [];
  const categoryId = trimmed(draft?.category_id);
  const parishId = trimmed(draft?.parish_id);
  const title = trimmed(draft?.title);
  const description = trimmed(draft?.description);
  const serviceArea = trimmed(draft?.service_area);
  const languages = compactArray(draft?.languages);
  const category = knownCategory(categories, categoryId);
  const priceType = trimmed(draft?.price_type) || "negotiable";
  const amount = priceAmount(draft?.price_amount);

  if (!provider?.id) {
    errors.push({ field: "provider", message: "Sign in with a provider account before submitting a listing." });
  }

  if (!title) {
    errors.push({ field: "title", message: "Service title is required." });
  }

  if (!categoryId || !category) {
    errors.push({ field: "category_id", message: "Choose a known launch category." });
  }

  if (description.length < 40) {
    errors.push({ field: "description", message: "Description should explain the service in at least 40 characters." });
  }

  if (!PRICE_TYPES.includes(priceType)) {
    errors.push({ field: "price_type", message: "Choose a supported price type." });
  }

  if (trimmed(draft?.price_amount) && (!Number.isFinite(amount) || amount <= 0)) {
    errors.push({ field: "price_amount", message: "Price amount must be a positive number when provided." });
  }

  if (!draft?.is_remote && !draft?.is_in_person) {
    errors.push({ field: "service_mode", message: "Choose remote, in-person, or both." });
  }

  if (!serviceArea) {
    errors.push({ field: "service_area", message: "Service area is required." });
  }

  if (!parishId || !knownParish(parishes, parishId)) {
    errors.push({ field: "parish_id", message: "Choose a known parish affiliation for review." });
  }

  if (languages.length === 0) {
    errors.push({ field: "languages", message: "Choose at least one service language." });
  }

  const safetyProfile = category ? getCategorySafetyProfile(category) : null;
  if (safetyProfile?.requiresCredentialPrompt && trimmed(draft?.license_info).length < 8) {
    errors.push({ field: "license_info", message: "Add credential or safety context for this category before review." });
  }

  if (safetyProfile?.requiresSensitiveAcknowledgement && !draft?.sensitive_acknowledged) {
    errors.push({ field: "sensitive_acknowledged", message: "Acknowledge the stronger safety disclaimer for this category." });
  }

  if (!draft?.agreed) {
    errors.push({ field: "agreed", message: "Accept the marketplace rules and no-endorsement disclaimer." });
  }

  return errors;
}

export function createPendingListingSubmission({
  provider,
  draft,
  categories = [],
  parishes = [],
  existingListings = [],
  now = new Date(),
}) {
  const errors = validateListingDraft(draft, { provider, categories, parishes });
  if (errors.length) {
    return { errors };
  }

  const category = knownCategory(categories, trimmed(draft.category_id));
  const safetyProfile = getCategorySafetyProfile(category);
  const createdAt = now.toISOString();
  const stamp = createdAt.replace(/\D/g, "");
  const baseSlug = slugify(draft.title) || "listing";
  const existingSlugs = new Set(existingListings.map((listing) => listing.slug).filter(Boolean));
  let slug = `${baseSlug}-${stamp.slice(0, 12)}`;
  let suffix = 2;
  while (existingSlugs.has(slug)) {
    slug = `${baseSlug}-${stamp.slice(0, 12)}-${suffix}`;
    suffix += 1;
  }

  return {
    id: `listing-${provider.id}-${stamp}`,
    provider_id: provider.id,
    category_id: trimmed(draft.category_id),
    parish_id: trimmed(draft.parish_id),
    slug,
    title: trimmed(draft.title),
    description: trimmed(draft.description),
    price_type: trimmed(draft.price_type) || "negotiable",
    price_amount: priceAmount(draft.price_amount),
    is_remote: Boolean(draft.is_remote),
    is_in_person: Boolean(draft.is_in_person),
    service_area: trimmed(draft.service_area),
    languages: compactArray(draft.languages),
    availability: trimmed(draft.availability),
    contact_pref: draft.contact_pref === "email" || draft.contact_pref === "phone" ? "share_after_contact" : "platform_only",
    photos: Number.isFinite(Number(draft.photos)) ? Math.max(0, Number(draft.photos)) : 0,
    tags: [],
    status: "pending_review",
    moderation_status: "pending",
    safety_level: safetyProfile.level,
    credential_summary: trimmed(draft.license_info) || null,
    no_endorsement_copy: LISTING_NO_ENDORSEMENT_COPY,
    rating: 0,
    review_count: 0,
    view_count: 0,
    is_featured: false,
    published_at: null,
    created_at: createdAt,
    updated_at: createdAt,
  };
}
